// ═══════════════════════════════════════════════════════════════════════════════
//  src/pages/DiagnosticoPage.jsx
//  MICROSERVICIO ONBOARDING v3 — Orquestador del "Motor de Diagnóstico"
//
//  Rutas: "/" (puerta de entrada de NSL) y "/diagnostico" (alias estable)
//  ────────────────────────────────────────────────────────────────────────────
//  ⚠️  NOTA DE RUTA — dos decisiones de producto encadenadas:
//
//   1. El documento maestro pide montar este flujo en /onboarding, pero esa
//      ruta YA está ocupada por src/pages/OnboardingPage.jsx, que es el
//      onboarding POST-REGISTRO (exige token y hace POST a
//      /api/profile/onboarding/complete). Son flujos distintos — este es
//      PRE-registro y anónimo — así que no pueden compartir ruta.
//
//   2. [20/08/2026] Por decisión del dueño del producto, este flujo es ahora
//      la PUERTA DE ENTRADA de NSL: "/" lo renderiza siempre, para todos.
//      Esto sustituye el mapa de rutas de Parte III §2 del documento, donde
//      "/" era la landing informativa; esa landing sigue intacta en /landing.
//      /diagnostico se mantiene como alias para los links de campañas.
//
//      Consecuencia asumida: un alumno con sesión que escriba la URL de NSL
//      también ve el diagnóstico. Los redirects internos que antes iban a "/"
//      (logout, fin del onboarding post-registro, "Para Estudiantes" del B2B,
//      logo de la landing) fueron recableados para no caer aquí.
//
//  AISLAMIENTO (Protocolo Central NSL §4 + Parte III §0 del documento):
//      · Página autónoma: no usa Header.jsx, ni ProtectedRoute, ni SubscriptionGuard.
//      · No modifica estilos globales — todo su CSS va prefijado con `.ob-`.
//      · El único punto de contacto con App.jsx es una línea de <Route>.
//
//  ORDEN DE PANTALLAS:  [0 → 0.1 → 0.2] → 1 → 2 → 3 → 4 → [5 → 5.5] → 6 → 7
//
//      · 0, 0.1, 0.2 = LOBBY DE BIENVENIDA. Añadido el 21/08/2026 por encargo
//        del dueño del producto: abrir preguntando "¿cuál es tu gran objetivo?"
//        a alguien recién llegado se sentía intrusivo. El lobby presenta NSL y
//        su equipamiento ANTES de pedir cualquier dato. No escribe payload.
//        Su copy vive en components/onboarding/lobbyContent.js y todavía NO
//        está en el documento maestro — ver nota en ese archivo.
//
//      · La bifurcación de Pantalla 4 ("Cero absoluto") salta 5 y 5.5 y va
//        directo a 6, tal como fija el documento.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useOnboardingState,
  patch,
  setStep,
  resetOnboarding,
  syncLead,
} from '../state/onboardingStore';

import ObHeader from '../components/onboarding/ObHeader';
import LobbyWelcome from '../components/onboarding/Screen0_Lobby/LobbyWelcome';
import LobbyEquipamiento from '../components/onboarding/Screen0_Lobby/LobbyEquipamiento';
import LobbyHandoff from '../components/onboarding/Screen0_Lobby/LobbyHandoff';
import Screen1_Identidad from '../components/onboarding/Screen1_Identidad';
import Screen2_Detonante from '../components/onboarding/Screen2_Detonante';
import Screen2_5_Intensidad from '../components/onboarding/Screen2_5_Intensidad';
import Screen3_Contexto from '../components/onboarding/Screen3_Contexto';
import Screen4_Autopercepcion from '../components/onboarding/Screen4_Autopercepcion';
import Screen4_5_Intentos from '../components/onboarding/Screen4_5_Intentos';
import CalibrationEngine from '../components/onboarding/Screen5_Calibracion/CalibrationEngine';
import CalibrationResult from '../components/onboarding/Screen5_Calibracion/CalibrationResult';
import Screen5_5_CapturaContacto from '../components/onboarding/Screen5_5_CapturaContacto';
import Screen6_EstiloEntrenamiento from '../components/onboarding/Screen6_EstiloEntrenamiento';
import Screen7_Revelacion from '../components/onboarding/Screen7_Revelacion';
import ReturnSummary from '../components/onboarding/ReturnSummary';

import ObPaginacion from '../components/onboarding/ObPaginacion';

import { PERCEIVED_LEVELS } from '../components/onboarding/onboardingContent';
// El sonido se engancha UNA vez, aquí, escuchando los clics en la raíz del
// módulo (ver sonidos.js). Engancharlo botón por botón garantizaría que el
// próximo botón que alguien añada saliera mudo sin que nadie se entere.
import { sonidoDeElemento } from '../components/onboarding/sonidos';
import '../components/onboarding/onboarding.css';

/* Pasos internos. Los decimales mantienen la numeración del documento. */
const STEP = {
  // Lobby de bienvenida — precede al diagnóstico y no pide ningún dato.
  LOBBY_WELCOME: 0,
  LOBBY_EQUIPAMIENTO: 0.1,
  LOBBY_HANDOFF: 0.2,

  IDENTIDAD: 1,
  DETONANTE: 2,
  // [24-09-2026] La intensidad y los intentos previos vivían apilados debajo
  // de la pregunta anterior, en la misma pantalla. Nacían fuera del área
  // visible, así que se contestaba sin verlos. Ahora cada pregunta es un paso.
  INTENSIDAD: 2.5,
  CONTEXTO: 3,
  AUTOPERCEPCION: 4,
  INTENTOS: 4.5,
  CALIBRACION: 5,
  CALIBRACION_RESULTADO: 5.2,
  CAPTURA_CONTACTO: 5.5,
  ESTILO_ENTRENAMIENTO: 6,
  REVELACION: 7,
  RESUMEN_RETORNO: 8,
};

/* El número que el alumno ve (y que dibuja la paginación) para cada pantalla
   interna. Las dos pantallas de consecuencia —el resultado de la calibración y
   la captura de contacto— comparten el número de la calibración: no son
   preguntas suyas, son lo que pasa después de ella. */
const PASO_DE = {
  [STEP.IDENTIDAD]: 1,
  [STEP.DETONANTE]: 2,
  [STEP.INTENSIDAD]: 3,
  [STEP.CONTEXTO]: 4,
  [STEP.AUTOPERCEPCION]: 5,
  [STEP.INTENTOS]: 6,
  [STEP.CALIBRACION]: 7,
  [STEP.ESTILO_ENTRENAMIENTO]: 8,
  [STEP.REVELACION]: 9,
};

const STEP_DE_PASO = Object.fromEntries(
  Object.entries(PASO_DE).map(([paso, numero]) => [numero, Number(paso)])
);

/* El camino hacia atrás. Desde el estilo de entrenamiento NO se vuelve a la
   captura de contacto: pedirle el correo dos veces al que ya dijo que no es
   exactamente lo que el documento maestro prohíbe en §5.bis. */
const ANTERIOR = {
  [STEP.IDENTIDAD]: STEP.LOBBY_HANDOFF,
  [STEP.DETONANTE]: STEP.IDENTIDAD,
  [STEP.INTENSIDAD]: STEP.DETONANTE,
  [STEP.CONTEXTO]: STEP.INTENSIDAD,
  [STEP.AUTOPERCEPCION]: STEP.CONTEXTO,
  [STEP.INTENTOS]: STEP.AUTOPERCEPCION,
  [STEP.CALIBRACION]: STEP.INTENTOS,
  [STEP.ESTILO_ENTRENAMIENTO]: STEP.INTENTOS,
};

/* Peso de cada paso en la barra de progreso. El lobby queda fuera a propósito:
   todavía no empieza el diagnóstico, así que mostrar una barra ahí sugeriría un
   compromiso que el usuario aún no ha adquirido. */
const PROGRESS = {
  [STEP.IDENTIDAD]: 11,
  [STEP.DETONANTE]: 22,
  [STEP.INTENSIDAD]: 33,
  [STEP.CONTEXTO]: 44,
  [STEP.AUTOPERCEPCION]: 55,
  [STEP.INTENTOS]: 66,
  [STEP.CALIBRACION]: 74,
  [STEP.CALIBRACION_RESULTADO]: 80,
  [STEP.CAPTURA_CONTACTO]: 86,
  [STEP.ESTILO_ENTRENAMIENTO]: 93,
  [STEP.REVELACION]: 100,
  [STEP.RESUMEN_RETORNO]: 100,
};

export default function DiagnosticoPage() {
  const payload = useOnboardingState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Quien ya vio su plan completo y no creó cuenta NUNCA vuelve a entrar al
  // arco narrativo: entra directo a la vista resumen (Parte I §7.3, "no
  // reinicia el flujo ni repite las 7 pantallas").
  //
  // Esto importa el doble desde que `/` es la puerta de entrada de NSL: sin
  // esta regla, cada visita de un recurrente reproduciría los ~12 segundos de
  // animación de procesamiento de la Pantalla 7 antes de mostrar nada.
  // El parámetro ?resume=summary del header sigue funcionando como atajo.
  const isCompleted = payload.funnel_stage_reached === 'completed_no_signup';
  const wantsSummary = isCompleted || searchParams.get('resume') === 'summary';

  // Se resuelve una sola vez, al montar. NO debe ser reactivo: al entrar a la
  // Pantalla 7 el funnel pasa a completed_no_signup, y un efecto que siguiera
  // a `wantsSummary` expulsaría al usuario al resumen justo antes de dejarle
  // ver la revelación que acaba de ganarse.
  const [step, setLocalStep] = useState(() =>
    wantsSummary ? STEP.RESUMEN_RETORNO : (payload._currentStep ?? STEP.LOBBY_WELCOME)
  );

  /* Reanudar exactamente donde se quedó (Parte III §1) — el estado ya viene
     rehidratado desde localStorage por el store. */
  const goTo = useCallback((nextStep) => {
    setLocalStep(nextStep);
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /* ── Handlers de flujo ─────────────────────────────────────────────────── */

  const handleAnswer = useCallback((partial) => {
    patch(partial);
  }, []);

  // Bifurcación del documento (Parte I §4): quien se declara "cero absoluto"
  // se salta la calibración. La decisión se toma tras los intentos previos,
  // que es la última pregunta antes del examen y la contesta todo el mundo.
  const saltaCalibracion = Boolean(
    PERCEIVED_LEVELS.find((l) => l.value === payload.perceived_level)?.skipsCalibration
  );

  const trasIntentos = useCallback(() => {
    goTo(saltaCalibracion ? STEP.ESTILO_ENTRENAMIENTO : STEP.CALIBRACION);
  }, [saltaCalibracion, goTo]);

  /* ── Navegación libre por los pasos ya contestados ─────────────────────── */

  const irAPaso = useCallback((numero) => {
    const destino = STEP_DE_PASO[numero];
    if (destino !== undefined) goTo(destino);
  }, [goTo]);

  const irAtras = useCallback(() => {
    goTo(ANTERIOR[step] ?? STEP.LOBBY_HANDOFF);
  }, [step, goTo]);

  const handleCalibrationComplete = useCallback(
    (result) => {
      patch({
        technical_score: result.technical_score,
        calibration_answers: result.answers,
        calibration_detail: result,
      });
      goTo(STEP.CALIBRACION_RESULTADO);
    },
    [goTo]
  );

  // Pantalla 5.5 — captura de contacto + único sync al backend (Punto A).
  const handleCapture = useCallback(
    async ({ email, phone, whatsappOk }) => {
      const partial = {
        contact_email: email,
        contact_phone: phone,
        // Tres estados, no dos. «captured_whatsapp_ok» es el único que
        // autoriza a escribirle por WhatsApp: se pone al pulsar el botón de
        // confirmación de la pantalla 5.5, y es el registro del permiso.
        // Un número guardado sin ese permiso es un dato de contacto pasivo.
        contact_soft_capture: whatsappOk ? 'captured_whatsapp_ok' : 'captured_after_p5',
      };
      patch(partial);
      await syncLead(partial);   // best-effort: un fallo no bloquea el flujo
      goTo(STEP.ESTILO_ENTRENAMIENTO);
    },
    [goTo]
  );

  // Salida no bloqueante de 5.5: registra "skipped" para que el equipo sepa
  // que ese perfil existe y dónde decidió no dejar sus datos (Parte I §5.bis).
  const handleSkipCapture = useCallback(async () => {
    const partial = { contact_soft_capture: 'skipped' };
    patch(partial);
    syncLead(partial);   // sin await: no debe retrasar al usuario que ya dijo que no
    goTo(STEP.ESTILO_ENTRENAMIENTO);
  }, [goTo]);

  // Al llegar a Pantalla 7 el funnel pasa a completed_no_signup: vio su plan
  // completo y todavía no creó cuenta (Parte I §7.3).
  const enterRevelacion = useCallback(() => {
    const partial = {
      route_assigned: 'custom_ai_plan',
      funnel_stage_reached: 'completed_no_signup',
    };
    patch(partial);
    syncLead(partial);
    goTo(STEP.REVELACION);
  }, [goTo]);

  // CTA "Quiero mi plan →" — el registro ya existe en el repo, no se toca.
  const handleConvert = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  const handleRestart = useCallback(() => {
    resetOnboarding();
    setLocalStep(STEP.LOBBY_WELCOME);
    window.scrollTo({ top: 0 });
  }, []);

  /* ── Render ────────────────────────────────────────────────────────────── */

  const renderStep = () => {
    switch (step) {
      /* ── Lobby de bienvenida (Pantallas 0 → 0.2) ────────────────────────── */

      case STEP.LOBBY_WELCOME:
        return <LobbyWelcome onNext={() => goTo(STEP.LOBBY_EQUIPAMIENTO)} />;

      case STEP.LOBBY_EQUIPAMIENTO:
        return (
          <LobbyEquipamiento
            onNext={() => goTo(STEP.LOBBY_HANDOFF)}
            onBack={() => goTo(STEP.LOBBY_WELCOME)}
          />
        );

      case STEP.LOBBY_HANDOFF:
        return (
          <LobbyHandoff
            onNext={() => goTo(STEP.IDENTIDAD)}
            onBack={() => goTo(STEP.LOBBY_EQUIPAMIENTO)}
          />
        );

      /* ── Diagnóstico (Pantallas 1 → 7) ──────────────────────────────────── */

      case STEP.IDENTIDAD:
        return (
          <Screen1_Identidad
            value={payload.aspiration}
            customValue={payload.aspiration_custom}
            onAnswer={handleAnswer}
            onNext={() => goTo(STEP.DETONANTE)}
          />
        );

      case STEP.DETONANTE:
        return (
          <Screen2_Detonante
            trigger={payload.trigger_event}
            triggerCustom={payload.trigger_event_custom}
            onAnswer={handleAnswer}
            onNext={() => goTo(STEP.INTENSIDAD)}
          />
        );

      case STEP.INTENSIDAD:
        return (
          <Screen2_5_Intensidad
            intensity={payload.training_intensity}
            onAnswer={handleAnswer}
            onNext={() => goTo(STEP.CONTEXTO)}
          />
        );

      case STEP.CONTEXTO:
        return (
          <Screen3_Contexto
            value={payload.context_type}
            onAnswer={handleAnswer}
            onNext={() => goTo(STEP.AUTOPERCEPCION)}
          />
        );

      case STEP.AUTOPERCEPCION:
        return (
          <Screen4_Autopercepcion
            level={payload.perceived_level}
            onAnswer={handleAnswer}
            onNext={() => goTo(STEP.INTENTOS)}
          />
        );

      case STEP.INTENTOS:
        return (
          <Screen4_5_Intentos
            attempts={payload.past_attempts}
            onAnswer={handleAnswer}
            onNext={trasIntentos}
          />
        );

      case STEP.CALIBRACION:
        return <CalibrationEngine onComplete={handleCalibrationComplete} />;

      case STEP.CALIBRACION_RESULTADO:
        return (
          <CalibrationResult
            perceivedLevel={payload.perceived_level}
            result={payload.calibration_detail ?? { technical_score: 0, estimated_cefr: '—' }}
            onNext={() => goTo(STEP.CAPTURA_CONTACTO)}
          />
        );

      case STEP.CAPTURA_CONTACTO:
        return (
          <Screen5_5_CapturaContacto onCapture={handleCapture} onSkip={handleSkipCapture} />
        );

      case STEP.ESTILO_ENTRENAMIENTO:
        return (
          <Screen6_EstiloEntrenamiento
            feedbackStyle={payload.feedback_style}
            onAnswer={handleAnswer}
            onNext={enterRevelacion}
          />
        );

      case STEP.REVELACION:
        return <Screen7_Revelacion payload={payload} onConvert={handleConvert} />;

      case STEP.RESUMEN_RETORNO:
        return (
          <ReturnSummary
            payload={payload}
            onConvert={handleConvert}
            onRestart={handleRestart}
          />
        );

      default:
        return <LobbyWelcome onNext={() => goTo(STEP.LOBBY_EQUIPAMIENTO)} />;
    }
  };

  const isLobby = step < STEP.IDENTIDAD;
  // La paginación acompaña a las ocho preguntas. Se retira en el resultado de
  // la calibración, en la captura de contacto y en el plan: ahí no hay nada que
  // contestar y ofrecer «Atrás» invitaría a deshacer algo que ya está hecho.
  const pasoVisible = PASO_DE[step];
  const conPaginacion = !isLobby && pasoVisible !== undefined && pasoVisible < 9;

  return (
    <div className="ob-root" onClickCapture={(e) => sonidoDeElemento(e.target)}>
      {/* Header propio del módulo: da salida a quien no es el público del
          diagnóstico (landing de alumnos, LP de empresas) y a quien ya tiene
          cuenta y solo quiere entrar a entrenar. Ver ObHeader.jsx. */}
      <ObHeader />

      {!isLobby && (
        <div
          className="ob-progress"
          role="progressbar"
          aria-valuenow={PROGRESS[step] ?? 0}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="ob-progress-fill" style={{ width: `${PROGRESS[step] ?? 0}%` }} />
        </div>
      )}

      {conPaginacion && (
        <ObPaginacion
          pasoActual={pasoVisible}
          payload={payload}
          saltaCalibracion={saltaCalibracion}
          onIr={irAPaso}
          onAtras={irAtras}
        />
      )}

      {renderStep()}
    </div>
  );
}
